// Canonical Groq streaming client for CatalystAgile PWAs.
// Each PWA's `ai-fallback.ts` (or equivalent) should delegate to streamGroq()
// and pass its own system + user messages. The key is fetched via getGroqKey()
// so it transparently picks up LocalStorage or .env values.

import { getGroqKey } from './useGroqKey'

const GROQ_URL = 'https://api.groq.com/openai/v1/chat/completions'

export interface GroqMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface GroqOptions {
  messages: GroqMessage[]
  model?: string
  temperature?: number
  /** Streaming callback — fires for each token chunk. */
  onStream?: (chunk: string) => void
  /** Abort signal for cancel buttons. */
  signal?: AbortSignal
}

export class GroqMissingKeyError extends Error {
  constructor() {
    super(
      'No Groq API key set. Open Settings (gear icon) and paste your key from console.groq.com/keys.'
    )
    this.name = 'GroqMissingKeyError'
  }
}

/**
 * Streams a Groq chat completion. Calls onStream for each delta token.
 * Returns the full concatenated response when the stream completes.
 * Throws GroqMissingKeyError if no key is configured.
 */
export async function streamGroq(opts: GroqOptions): Promise<string> {
  const key = getGroqKey()
  if (!key) throw new GroqMissingKeyError()

  const res = await fetch(GROQ_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${key}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: opts.model ?? 'llama-3.3-70b-versatile',
      messages: opts.messages,
      temperature: opts.temperature ?? 0.3,
      stream: true,
    }),
    signal: opts.signal,
  })

  if (!res.ok) {
    const errBody = await res.text().catch(() => '')
    throw new Error(`Groq API ${res.status}: ${errBody || res.statusText}`)
  }

  const reader = res.body?.getReader()
  if (!reader) throw new Error('Groq returned empty body')
  const decoder = new TextDecoder()
  let full = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    const chunk = decoder.decode(value)
    for (const line of chunk.split('\n')) {
      const trimmed = line.trim()
      if (!trimmed.startsWith('data: ')) continue
      const payload = trimmed.replace('data: ', '')
      if (payload === '[DONE]') continue
      try {
        const parsed = JSON.parse(payload)
        const delta = parsed.choices?.[0]?.delta?.content
        if (typeof delta === 'string' && delta.length) {
          full += delta
          opts.onStream?.(delta)
        }
      } catch {
        // Stream chunks can arrive mid-token; ignore parse failures.
      }
    }
  }

  return full
}
